export interface ScreenResolution {
  width: number;
  height: number;
  label: string;
  aspectRatio: string;
  exportWidth: number;
  exportHeight: number;
}

export type AppTheme = 'dark' | 'light' | 'pxlvd';

export interface Settings {
  projectName?: string;
  currentProjectId?: string;
  Resolution: ScreenResolution;
  ColorDepth?: number;
  currentColor?: number;
  cellSize: number;
  theme: AppTheme;
  activePaletteId: string;
  paletteColorCount: number;
  customPalettes: { id: string; name: string; colors: string[] }[];
  gridLineWidth: number;
  undoHistoryDepth: number;  // 20 | 50 | 100 | 200
  onionSkinEnabled: boolean;
  onionSkinFrames: number;   // how many prev/next frames to ghost
  onionSkinOpacity: number;  // 0-50
  fps: number;
  layerPanelWidth?: number;  // px width of the timeline layer-header column
}

export const defaultSetting: Settings = {
  projectName: 'Untitled Project',
  Resolution: { width: 54, height: 96, label: 'Portrait (Reels/TikTok)', aspectRatio: '9:16', exportWidth: 1080, exportHeight: 1920 },
  ColorDepth: 8,
  cellSize: 8,
  theme: 'pxlvd',
  activePaletteId: 'dawnbringer16',
  paletteColorCount: 16,
  customPalettes: [],
  gridLineWidth: 1,
  undoHistoryDepth: 50,
  onionSkinEnabled: false,
  onionSkinFrames: 2,
  onionSkinOpacity: 20,
  fps: 12,
  layerPanelWidth: 240,
};

export const defaultSettings: ScreenResolution[] = [
  { width: 27, height: 48,   label: 'Portrait SM',        aspectRatio: '9:16',  exportWidth: 1080, exportHeight: 1920 },
  { width: 54, height: 96,   label: 'Portrait (Reels)',    aspectRatio: '9:16',  exportWidth: 1080, exportHeight: 1920 },
  { width: 108, height: 192, label: 'Portrait HQ',         aspectRatio: '9:16',  exportWidth: 1080, exportHeight: 1920 },
  { width: 32, height: 32,   label: 'Square SM',           aspectRatio: '1:1',   exportWidth: 1080, exportHeight: 1080 },
  { width: 64, height: 64,   label: 'Square (Instagram)',  aspectRatio: '1:1',   exportWidth: 1080, exportHeight: 1080 },
  { width: 128, height: 128, label: 'Square HQ',           aspectRatio: '1:1',   exportWidth: 1080, exportHeight: 1080 },
  { width: 48, height: 27,   label: 'Landscape SM',        aspectRatio: '16:9',  exportWidth: 1920, exportHeight: 1080 },
  { width: 96, height: 54,   label: 'Landscape (YouTube)', aspectRatio: '16:9',  exportWidth: 1920, exportHeight: 1080 },
  { width: 192, height: 108, label: 'Landscape HQ',        aspectRatio: '16:9',  exportWidth: 1920, exportHeight: 1080 },
  { width: 16, height: 16,   label: 'Micro (16×16)',        aspectRatio: '1:1',   exportWidth: 512,  exportHeight: 512  },
  { width: 32, height: 32,   label: 'Classic (32×32)',      aspectRatio: '1:1',   exportWidth: 512,  exportHeight: 512  },
];

/** Extra choice for new-project UIs (not a fixed size until user enters W×H). */
export const CUSTOM_RESOLUTION_PRESET: ScreenResolution = {
  width: 64,
  height: 64,
  label: 'Custom',
  aspectRatio: 'Custom',
  exportWidth: 512,
  exportHeight: 512,
};

export const newProjectResolutionPresets: ScreenResolution[] = [
  ...defaultSettings,
  CUSTOM_RESOLUTION_PRESET,
];

export const CANVAS_DIM_MIN = 4;
/** Maximum canvas width/height (8K). Very large canvases may be slow or run out of memory in the browser. */
export const CANVAS_DIM_MAX = 8192;

export function isCustomResolutionPreset(r: ScreenResolution): boolean {
  return r.label === 'Custom';
}

/** True if `selected` was chosen as the same card as `card` in the new-project grid. */
export function isSameNewProjectResolutionCard(selected: ScreenResolution, card: ScreenResolution): boolean {
  if (isCustomResolutionPreset(card)) return isCustomResolutionPreset(selected);
  return !isCustomResolutionPreset(selected) && selected.label === card.label;
}

export function clampCanvasDimension(n: number): number {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x)) return CANVAS_DIM_MIN;
  return Math.max(CANVAS_DIM_MIN, Math.min(CANVAS_DIM_MAX, x));
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/** Build a full ScreenResolution from custom W×H (export size scales with canvas, capped). */
export function resolutionFromCustomSize(width: number, height: number): ScreenResolution {
  const w = clampCanvasDimension(width);
  const h = clampCanvasDimension(height);
  const longEdge = Math.max(w, h);
  const scale = longEdge > 0 ? Math.min(1920 / longEdge, 40) : 20;
  const g = gcd(w, h);
  return {
    width: w,
    height: h,
    label: 'Custom',
    aspectRatio: `${w / g}:${h / g}`,
    exportWidth: Math.max(1, Math.round(w * scale)),
    exportHeight: Math.max(1, Math.round(h * scale)),
  };
}

export const cellSizeOptions: { value: number; label: string }[] = [
  { value: 4,  label: '4px' },
  { value: 6,  label: '6px' },
  { value: 8,  label: '8px' },
  { value: 10, label: '10px' },
  { value: 14, label: '14px' },
  { value: 18, label: '18px' },
  { value: 24, label: '24px' },
];

export const colorDepthOptions: number[] = [1, 2, 4, 8, 16, 24];
